import { ReactNode, createContext, useContext, useRef } from 'react';

export enum AnalyticsQueueNames {
  WEDDING_WEBSITE_DETAILS_FAQ = 'weddingWebsiteDetailsFaq',
}

export interface IGenericAnalyticsItem {
  event?: string;
  [key: string]: string | undefined;
}

export type AnalyticsQueues = Partial<Record<AnalyticsQueueNames, IGenericAnalyticsItem[]>>;

interface AnalyticsContextValue {
  analyticsQueues: React.MutableRefObject<AnalyticsQueues>;
  addToAnalyticsQueue: (queueName: AnalyticsQueueNames, item: IGenericAnalyticsItem) => void;
  getAnalyticsQueue: (queueName: AnalyticsQueueNames) => IGenericAnalyticsItem[];
  clearAnalyticsQueue: (queueName: AnalyticsQueueNames) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

interface AnalyticsProviderProps {
  children: ReactNode;
}

/**
 * Provider component that gives access to analytics queues throughout the app
 * The ref allows storing analytics data without triggering re-renders
 * The queues are used to store (and hold) events that need to be sent to the server later only when user saves the website changes.
 * The queues are identified by a string key. So it's possible to have multiple queues if needed.
 * This Context doesn't send any analytics. It's only as a temporary store.
 */
export const AnalyticsQueueProvider = ({ children }: AnalyticsProviderProps) => {
  const analyticsQueues = useRef<AnalyticsQueues>({});

  const addToAnalyticsQueue = (queueName: AnalyticsQueueNames, item: IGenericAnalyticsItem) => {
    if (!analyticsQueues.current[queueName]) {
      analyticsQueues.current[queueName] = [];
    }
    analyticsQueues.current[queueName]?.push(item);
  };

  const getAnalyticsQueue = (queueName: AnalyticsQueueNames) =>
    analyticsQueues.current[queueName] || [];

  const clearAnalyticsQueue = (queueName: AnalyticsQueueNames) => {
    analyticsQueues.current[queueName] = [];
  };

  return (
    <AnalyticsContext.Provider
      value={{
        analyticsQueues,
        addToAnalyticsQueue,
        getAnalyticsQueue,
        clearAnalyticsQueue,
      }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

/**
 * Hook to access analytics queues context
 * @throws Error if used outside of AnalyticsProvider
 */
export const useAnalyticsQueue = () => {
  const context = useContext(AnalyticsContext);

  if (!context) {
    throw new Error('useAnalyticsQueue must be used within an AnalyticsQueueProvider');
  }

  return context;
};
